/**
 * What the editor opens with: an empty score, or one handed over with the navigation.
 *
 * Kept out of the page because it is the one piece of the board that runs before anything
 * is on the screen and has nothing to do with the rest of it: the page's own job starts once
 * there is a document.
 */

import { applyAll, createScore, fromMusicXml, instrumentBy, setup } from "@app/score-editor";
import type { Score } from "@app/score-editor";

/** Eight empty bars of piano and violin: enough to type into without choosing anything first. */
export function startingScore(): Score {
  const empty = createScore("Untitled score", { measures: 8 });
  const presets = ["violin", "piano"].map(instrumentBy);
  const commands = presets.flatMap((preset, index) =>
    preset === null ? [] : [setup.addPart(empty, preset, index)],
  );
  const built = applyAll(empty, commands, "start a score");
  // The commands are built from the presets this file names, so a refusal would mean one of
  // those names is wrong rather than anything the user did: open the empty score and let the
  // parts be added by hand.
  return built.ok ? built.score : empty;
}

/** The MusicXML another board handed over with the navigation, if it did. */
export function handedOver(state: unknown): string | null {
  if (typeof state !== "object" || state === null) return null;
  const given = (state as { musicxml?: unknown }).musicxml;
  return typeof given === "string" && given.trim() !== "" ? given : null;
}

/** The handed-over score, or the empty one and the reason the handover could not be read. */
export function opening(given: string | null): { score: Score; refused: string | null } {
  if (given === null) return { score: startingScore(), refused: null };
  const read = fromMusicXml(given);
  // A score this app wrote itself and its own importer will not read is a bug worth seeing
  // rather than hiding, so the reason is carried onto the page instead of becoming a blank.
  if (!read.ok) return { score: startingScore(), refused: read.reason };
  return { score: read.score, refused: null };
}
